import React, { useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

export const Navbar = () => {
  const [openDropdown, setOpenDropdown] = useState(null);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const location = useLocation();
  const closeTimeoutRef = useRef(null);

  /* ------------------------------------------------------------------ menus */

  const technologyItems = [
    { to: '/technology#ctf-fusion', label: 'CTF Fusion Core' },
    { to: '/technology#foundation-models', label: 'Foundation Models' },
    { to: '/technology#xai', label: 'Explainable AI' },
    { to: '/technology#knowledge-graph', label: 'Knowledge Graph AI' },
    { to: '/technology#multi-omics', label: 'Multi-Omics Intelligence' },
    { to: '/technology#clinical-reasoning', label: 'Clinical Reasoning' },
  ];

  const solutionsItems = [
    { to: '/solutions/respiratory-screening', label: 'Diagnostics' },
    { to: '/solutions/clinical-intelligence', label: 'Clinical Intelligence' },
    { to: '/solutions/precision-medicine', label: 'Precision Medicine' },
    { to: '/solutions/monitoring', label: 'Monitoring' },
  ];

  // New: Agents is a separate top-level category from Solutions.
  // Currently holds a single item (DI Research Agent) — this is a deliberate
  // positioning choice, not a claim of a mature agent product family.
  const agentsItems = [
    { to: '/agents/research-agent', label: 'DI Research Agent' },
  ];

  // Insights now lives here instead of as a standalone top-level link.
  // Placement assumption: Insights placed right after FAQ, ahead of the
  // private documents. Move this line if a different order is preferred.
  const resourcesItems = [
    { to: '/faq', label: 'FAQ' },
    { to: '/insights', label: 'Insights' },
    { to: '/resources/documents/platform-overview', label: 'Platform Overview', badge: 'Private' },
    { to: '/resources/documents/master-deck', label: 'Master Deck', badge: 'Private' },
  ];

  const companyItems = [
    { to: '/company/about', label: 'About' },
    { to: '/company/mission', label: 'Mission' },
    { to: '/company/leadership', label: 'Leadership' },
    { to: '/company/roadmap', label: 'Roadmap' },
    { to: '/company/scientific-advisory-board', label: 'Advisory Board' },
    { to: '/company/partnerships', label: 'Partnerships' },
    { to: '/company/careers', label: 'Careers' },
    { to: '/company/news', label: 'News' },
    { to: '/company/contact', label: 'Contact' },
  ];

  /* ---------------------------------------------------------------- actions */

  const toggleDropdown = (name) => {
    setOpenDropdown(openDropdown === name ? null : name);
  };

  const closeAll = () => {
    setOpenDropdown(null);
    setIsMobileOpen(false);
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  /* --------------------------------------------------------------- renderers
     These are plain functions that return JSX (called as {renderX(...)}),
     not components. Defining components inside another component makes React
     recreate them on every render, which drops keyboard focus and stops the
     open/close transitions from animating. */

  // Top-level nav link: teal on hover/active, with an animated underline
  const renderNavLink = (to, label) => {
    const isActive = location.pathname === to;
    return (
      <Link
        to={to}
        className={`group relative inline-flex items-center text-sm font-semibold tracking-tight whitespace-nowrap transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#187F73]/40 rounded-sm ${
          isActive ? 'text-[#187F73]' : 'text-slate-700 hover:text-[#187F73]'
        }`}
        onClick={closeAll}
      >
        {label}
        <span
          className={`absolute left-0 -bottom-1 h-0.5 bg-[#187F73] rounded-full transition-all duration-200 ${
            isActive ? 'w-full' : 'w-0 group-hover:w-full'
          }`}
        />
      </Link>
    );
  };

  // Dropdown trigger: teal + underline when open OR when the current route belongs to this section
  const renderDropdown = (title, items) => {
    const isOpen = openDropdown === title;
    const sectionActive = items.some((item) =>
      location.pathname.startsWith(item.to.split('#')[0])
    );
    const highlighted = isOpen || sectionActive;

    const handleMouseEnter = () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
      setOpenDropdown(title);
    };

    const handleMouseLeave = () => {
      closeTimeoutRef.current = setTimeout(() => {
        setOpenDropdown(null);
      }, 150);
    };

    // Escape closes the menu and returns focus to its trigger
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        setOpenDropdown(null);
        const trigger = e.currentTarget.querySelector('button');
        if (trigger) trigger.focus();
      }
    };

    // Tabbing out of the menu closes it
    const handleBlur = (e) => {
      if (!e.currentTarget.contains(e.relatedTarget)) {
        setOpenDropdown((current) => (current === title ? null : current));
      }
    };

    return (
      <div
        className="relative"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
      >
        <button
          type="button"
          onClick={() => toggleDropdown(title)}
          aria-haspopup="true"
          aria-expanded={isOpen}
          className={`group relative flex items-center gap-1.5 text-sm font-semibold tracking-tight whitespace-nowrap transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#187F73]/40 rounded-sm ${
            highlighted ? 'text-[#187F73]' : 'text-slate-700 hover:text-[#187F73]'
          }`}
        >
          {title}
          <span
            aria-hidden="true"
            className={`text-[10px] transition-transform duration-200 ${
              isOpen ? 'rotate-180' : ''
            }`}
          >
            ▼
          </span>
          <span
            className={`absolute left-0 -bottom-1 h-0.5 bg-[#187F73] rounded-full transition-all duration-200 ${
              highlighted ? 'w-full' : 'w-0 group-hover:w-full'
            }`}
          />
        </button>

        {/* Closed menus are "invisible" so their links leave the tab order */}
        <div
          className={`absolute left-0 mt-2 w-56 origin-top bg-white shadow-xl rounded-xl border border-slate-200 py-2 z-50 transition-all duration-150 ${
            isOpen
              ? 'visible opacity-100 scale-100 translate-y-0 pointer-events-auto'
              : 'invisible opacity-0 scale-95 -translate-y-1 pointer-events-none'
          }`}
        >
          {items.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className="flex items-center px-4 py-2.5 text-sm font-medium text-slate-700 hover:text-white hover:bg-[#187F73] transition-colors duration-150 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#187F73]/40"
              onClick={closeAll}
            >
              {item.label}
              {item.badge && (
                <span className="ml-auto pl-3 text-[10px] font-semibold opacity-70">
                  {item.badge}
                </span>
              )}
            </Link>
          ))}
        </div>
      </div>
    );
  };

  /* Mobile helpers */
  const mobileLinkClass = (to) =>
    `block text-sm font-semibold py-2 transition-colors ${
      location.pathname === to
        ? 'text-[#187F73] border-l-4 border-[#187F73] pl-3'
        : 'text-slate-700 pl-4 hover:text-[#187F73]'
    }`;

  const renderMobileSection = (title, items) => (
    <div className="pt-2 border-t border-slate-100">
      <div className="font-bold text-slate-400 text-xs uppercase tracking-wider px-4 py-1">{title}</div>
      {items.map((item) => (
        <Link
          key={item.to}
          to={item.to}
          onClick={closeAll}
          className="flex items-center justify-between pl-6 pr-4 py-1.5 text-sm font-medium text-slate-700 hover:text-white hover:bg-[#187F73] transition-colors rounded-lg"
        >
          {item.label}
          {item.badge && (
            <span className="text-[10px] font-semibold opacity-70">{item.badge}</span>
          )}
        </Link>
      ))}
    </div>
  );

  /* ----------------------------------------------------------------- render */

  return (
    <nav className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 gap-4">
          {/* Logo: never shrinks or wraps, so title + "PLATFORM" stay on one tidy block */}
          <Link to="/" className="flex items-center gap-3 shrink-0" onClick={closeAll}>
            <div className="w-11 h-11 rounded-2xl bg-[#187F73] flex items-center justify-center shadow-md shadow-[#187F73]/30 ring-1 ring-[#187F73]/60 overflow-hidden shrink-0">
              <img
                src="/logo192.png"
                alt="Diagnostic Intelligence"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex flex-col leading-tight whitespace-nowrap">
              <div className="text-base xl:text-lg font-black text-[#187F73] tracking-tight">
                Diagnostic Intelligence
              </div>
              <div className="text-[10px] font-extrabold tracking-[0.28em] uppercase text-slate-400">
                Platform
              </div>
            </div>
          </Link>

          {/* Desktop nav: Home, Platform, Technology, Solutions, Agents, Company, Resources, Interactive Demo */}
          <div className="hidden xl:flex items-center gap-7">
            {renderNavLink('/', 'Home')}
            {renderNavLink('/platform', 'Platform')}
            {renderDropdown('Technology', technologyItems)}
            {renderDropdown('Solutions', solutionsItems)}
            {renderDropdown('Agents', agentsItems)}
            {renderDropdown('Company', companyItems)}
            {renderDropdown('Resources', resourcesItems)}
            <Link
              to="/interactive-demo"
              onClick={closeAll}
              className="shrink-0 text-white text-sm font-bold bg-[#187F73] px-4 py-1.5 rounded-xl border border-[#187F73] whitespace-nowrap transition-colors duration-200 hover:bg-[#15695F] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#187F73]/40"
            >
              Interactive Demo
            </Link>
          </div>

          {/* Mobile menu toggle */}
          <button
            type="button"
            className="xl:hidden text-2xl text-slate-700 hover:text-[#187F73] transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#187F73]/40 rounded-sm"
            onClick={() => setIsMobileOpen(!isMobileOpen)}
            aria-label="Toggle menu"
            aria-expanded={isMobileOpen}
          >
            ☰
          </button>
        </div>

        {/* Mobile menu (same order as desktop) */}
        {isMobileOpen && (
          <div className="xl:hidden py-4 border-t border-slate-200 space-y-1 max-h-[80vh] overflow-y-auto">
            <Link to="/" onClick={closeAll} className={mobileLinkClass('/')}>
              Home
            </Link>
            <Link to="/platform" onClick={closeAll} className={mobileLinkClass('/platform')}>
              Platform
            </Link>

            {renderMobileSection('Technology', technologyItems)}
            {renderMobileSection('Solutions', solutionsItems)}
            {renderMobileSection('Agents', agentsItems)}
            {renderMobileSection('Company', companyItems)}
            {renderMobileSection('Resources', resourcesItems)}

            <Link
              to="/interactive-demo"
              onClick={closeAll}
              className="block font-bold text-white bg-[#187F73] mx-4 my-2 px-3 py-2 rounded-lg text-center transition-colors hover:bg-[#15695F]"
            >
              Interactive Demo
            </Link>
          </div>
        )}
      </div>
    </nav>
  );
};
